import Box from '@cloudscape-design/components/box'
import ColumnLayout from '@cloudscape-design/components/column-layout'
import LineChart from '@cloudscape-design/components/line-chart'
import Link from '@cloudscape-design/components/link'
import Spinner from '@cloudscape-design/components/spinner'
import type { EmailResponseStats } from '@/lib/types'
import AIAnnotation from './AIAnnotation'
import { InsightBullet } from '../../../lib/types'

interface Props {
  stats: EmailResponseStats | null
  loading: boolean
  insights?: InsightBullet[]
}

export default function EmailResponsesWidget({ stats, loading, insights }: Props) {
  if (loading) {
    return (
      <Box textAlign="center" padding="xl">
        <Spinner size="large" nativeAttributes={{ 'aria-label': 'Loading' }} />
      </Box>
    )
  }
  if (!stats) {
    return (
      <Box textAlign="center" padding="l" color="text-body-secondary">
        No data available
      </Box>
    )
  }

  const sentData = stats.byDay.map(d => ({ x: new Date(d.date), y: d.sent }))
  const repliedData = stats.byDay.map(d => ({ x: new Date(d.date), y: d.replied }))
  const maxY = Math.max(...stats.byDay.map(d => d.sent), 1)

  return (
    <Box>
      {!stats.emailConnected && (
        <Box padding={{ bottom: 's' }} color="text-status-warning">
          Connect Gmail or Outlook in <Link href="/settings/platforms">Settings</Link> to send email outreach and track replies.
        </Box>
      )}
      <ColumnLayout columns={2} variant="text-grid">
        <Box>
          <Box variant="awsui-key-label">Emails Sent</Box>
          <Box variant="h2">{stats.totalSent}</Box>
        </Box>
        <Box>
          <Box variant="awsui-key-label">Reply Rate</Box>
          <Box variant="h2">{stats.replyRate.toFixed(1)}%</Box>
        </Box>
      </ColumnLayout>
      <LineChart
        series={[
          { title: 'Sent',    type: 'line', data: sentData },
          { title: 'Replied', type: 'line', data: repliedData },
        ]}
        xScaleType="time"
        yDomain={[0, maxY + 2]}
        height={160}
        i18nStrings={{
          filterLabel: 'Filter series',
          filterPlaceholder: 'Filter series',
          filterSelectedAriaLabel: '(selected)',
          legendAriaLabel: 'Legend',
          chartAriaRoleDescription: 'line chart',
          xTickFormatter: (d) =>
            (d as Date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          yTickFormatter: (v) => typeof v === 'number' ? v.toLocaleString() : String(v),
        }}
        ariaLabel="Email response trend"
      />
      <AIAnnotation bullets={insights ?? []} />
    </Box>
  )
}
